// Story 1.27 — TanStack Query hook for GET /api/v1/admin/creator-applications.
//
// Backs the AdminApplicationsPage queue list. Status defaults to
// 'pending' (matches the server's default). Filters serialize into the
// query key so the cache differentiates between filter combinations.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type AdminCreatorApplicationStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "withdrawn"
  | "any";

export type AdminApplicationsFilters = {
  status: AdminCreatorApplicationStatus;
  submitted_since?: string;
  submitted_until?: string;
  display_name_search?: string;
  page: number;
  page_size: number;
};

export const DEFAULT_FILTERS: AdminApplicationsFilters = {
  status: "pending",
  page: 1,
  page_size: 50,
};

export type AdminApplicationsListItem = {
  application_id: string;
  user_id: string;
  display_name: string;
  hello_text: string;
  region: string;
  language: string;
  status: "pending" | "approved" | "rejected" | "withdrawn";
  submitted_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  applicant_email: string;
  applicant_email_verified: boolean;
  applicant_account_created_at: string;
};

export type AdminApplicationsListResponse = {
  data: AdminApplicationsListItem[];
  page: number;
  page_size: number;
  has_next: boolean;
  total_pending: number | null;
};

function buildQueryString(filters: AdminApplicationsFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.submitted_since)
    params.set("submitted_since", filters.submitted_since);
  if (filters.submitted_until)
    params.set("submitted_until", filters.submitted_until);
  if (filters.display_name_search)
    params.set("display_name_search", filters.display_name_search);
  params.set("page", String(filters.page));
  params.set("page_size", String(filters.page_size));
  return params.toString();
}

async function fetchAdminApplications(
  filters: AdminApplicationsFilters
): Promise<AdminApplicationsListResponse> {
  const qs = buildQueryString(filters);
  const response = await fetch(`/api/v1/admin/creator-applications?${qs}`, {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Admin queue fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as AdminApplicationsListResponse;
}

export function useAdminCreatorApplications(filters: AdminApplicationsFilters) {
  return useQuery<AdminApplicationsListResponse, Error>({
    queryKey: ["admin-creator-applications", filters],
    queryFn: () => fetchAdminApplications(filters),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}
